import React, { useEffect, useMemo, useRef, useState } from 'react';
import Image from 'react-native-fast-image';
import {
    View,
    ScrollView,
    Animated,
    Easing,
    StyleSheet,
    LayoutChangeEvent,
} from 'react-native';
import { trpc } from '../../../../apiCalls/trpcClient';
import { AgeGroup } from '@sapphicsavvy/business';
import { utils } from '../../../../utils/utils';

interface Props {
    ageGroup?: AgeGroup;
}

export const AutoScrollingAvatars = ({ ageGroup }: Props) => {
    const scrollViewRef = useRef<ScrollView | null>(null);
    const scrollX = useRef(new Animated.Value(0)).current;
    const [contentWidth, setContentWidth] = useState(0);
    const [containerWidth, setContainerWidth] = useState(0);

    const { data: users } = trpc.sapphics.listOfUsers.useQuery({
        filter: {
            age: ageGroup ? utils.ageGroupToAgeFilter(ageGroup) : undefined,
        },
    });

    const avatars = useMemo(
        () =>
            users?.items.map((user) => ({
                uri: user.avatarUrl?.original,
                id: user.id,
            })),
        [users],
    );

    useEffect(() => {
        if (avatars && avatars.length) {
            Image.preload(
                avatars
                    .map(({ uri }) => ({ uri: uri! }))
                    .filter((item) => !!item.uri),
            );
        }
    }, [avatars]);

    useEffect(() => {
        if (contentWidth > containerWidth) {
            scrollX.setValue(0);
            const maxScroll = contentWidth - containerWidth;
            const timeout = setTimeout(() => {
                Animated.timing(scrollX, {
                    toValue: maxScroll,
                    duration: 1000,
                    easing: Easing.linear,
                    useNativeDriver: false,
                }).start();
            }, 3000);
            return () => clearTimeout(timeout);
        }
    }, [contentWidth, containerWidth]);

    useEffect(() => {
        const id = scrollX.addListener(({ value }) => {
            scrollViewRef.current?.scrollTo({ x: value, animated: false });
        });
        return () => {
            scrollX.removeListener(id);
        };
    }, []);

    return (
        <View
            style={styles.container}
            onLayout={(e: LayoutChangeEvent) =>
                setContainerWidth(e.nativeEvent.layout.width)
            }
        >
            <ScrollView
                ref={scrollViewRef}
                horizontal
                showsHorizontalScrollIndicator={false}
                scrollEnabled={false}
                contentContainerStyle={styles.scrollContent}
                onContentSizeChange={(w) => setContentWidth(w)}
            >
                {avatars?.map((avatar) => (
                    <View key={avatar.id} style={styles.avatarContainer}>
                        <Image
                            source={{ uri: avatar.uri }}
                            style={styles.avatar}
                            accessibilityLabel="User Avatar"
                        />
                    </View>
                ))}
            </ScrollView>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        height: 50,
        backgroundColor: '#bed7e6',
    },
    scrollContent: {
        alignItems: 'center',
        paddingHorizontal: 10,
    },
    avatarContainer: {
        marginHorizontal: 6,
        borderRadius: 40,
        overflow: 'hidden',
        borderWidth: 2,
        borderColor: 'white',
    },
    avatar: {
        width: 40,
        height: 40,
        borderRadius: 40,
    },
});
